import { FC } from 'react';
import { css, CSSProperties } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Heading, Link, LinkStyle, LinkTarget } from '@atlassian/learning-components';

import { getAssetUrl } from 'src/services/http/user';

export interface ContactUsSectionProps {
  containerStyles?: CSSProperties;
  title: string;
  subtitle: string;
  linkUrl: string;
  linkLabel: string;
  linkTarget?: LinkTarget;
  onNavigate?: (link?: string, sectionTitle?: string, buttonLabel?: string, target?: LinkTarget) => void;
}

export const ContactUsSection: FC<ContactUsSectionProps> = ({
  containerStyles,
  title,
  subtitle,
  linkUrl,
  linkLabel,
  linkTarget,
  onNavigate,
}) => (
  <section css={[contactSectionStyles, marginBottomStyles]} style={containerStyles}>
    <div css={contactContainerStyles}>
      <div css={contactTextContainerStyles}>
        <div css={contactHeadingStyles}>
          <Heading level={2}>{title}</Heading>
        </div>
        <p css={contactTextStyles}>{subtitle}</p>
        <div css={linkFontStyles}>
          <Link
            data-testid="contact-us-button"
            link={linkUrl}
            style={LinkStyle.SEMIBOLD}
            label={linkLabel}
            icon="arrow-right"
            target={linkTarget}
            onClick={() => onNavigate?.(linkUrl, title, linkLabel, linkTarget)}
          />
        </div>
      </div>
      <img src={getAssetUrl('/images/certificationDetail/message.svg')} alt="message icon" css={contactImgStyles} />
    </div>
  </section>
);

const linkFontStyles = css({
  a: {
    fontSize: '20px',
    display: 'flex',
    justifyContent: 'center',
  },
});

const marginBottomStyles = css({
  marginBottom: token('space.1000'),

  [media.below.sm]: {
    marginBottom: token('space.500'),
  },
});

const contactSectionStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

const contactContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: '500px 440px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: token('space.1000'),

  [media.below.md]: {
    gridTemplateColumns: '1fr',
    gap: token('space.600'),
    maxWidth: '720px',
  },

  [media.below.sm]: {
    gap: `${token('space.300')} !important`,
  },
});

const contactImgStyles = css({
  width: '442px',
  height: 'auto',

  [media.below.md]: {
    margin: '0 auto',
    order: -1,
  },

  [media.below.sm]: {
    width: '343px',
    height: '215px',
  },
});

const contactTextContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',

  [media.below.md]: {
    alignItems: 'center',
  },
});

const contactHeadingStyles = css({
  marginBottom: token('space.200'),

  [media.below.md]: {
    textAlign: 'center',
  },
});

const contactTextStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '32px',
  marginBottom: token('space.500'),
  marginTop: 0,

  [media.below.md]: {
    textAlign: 'center',
  },

  [media.below.sm]: {
    fontSize: '16px',
    lineHeight: '24px',
  },
});
